import { LucideIcon, Video, Zap, Code, Share2, Presentation, Phone, Target, Compass, Lightbulb, Shield } from "lucide-react";

export interface Category {
  id: string;
  slug: string;
  name: string;
  icon: LucideIcon;
  description: string;
  longDescription: string;
  color: string;
  benefits: string[];
  targetAudience: string[];
}

export const categories: Category[] = [
  {
    id: "contenido-visual",
    slug: "contenido-visual",
    name: "Contenido Visual",
    icon: Video,
    description: "Crea videos, diseños e imágenes profesionales sin equipo caro ni conocimientos técnicos",
    longDescription: "Genera contenido visual de alta calidad para redes sociales, web y marketing. Desde videos completos con voz y personajes hasta diseños gráficos profesionales. Todo sin cámara, sin editor, sin diseñador.",
    color: "from-purple-500 to-pink-500",
    benefits: [
      "Videos profesionales en minutos",
      "Diseños para redes sin Photoshop",
      "Ahorra 300-500€ semanales en producción",
      "Sin conocimientos técnicos necesarios"
    ],
    targetAudience: ["Marketing", "Comunicación", "Emprendedores", "PYMEs"]
  },
  {
    id: "automatizacion",
    slug: "automatizacion",
    name: "Automatización",
    icon: Zap,
    description: "Libera 10-15 horas semanales automatizando emails, facturas y tareas repetitivas",
    longDescription: "Automatiza todo lo que te quita tiempo: respuestas de emails, procesamiento de facturas, gestión de cobros y seguimiento administrativo. La IA trabaja 24/7 mientras tú duermes.",
    color: "from-blue-500 to-cyan-500",
    benefits: [
      "Recupera 10-15 horas cada semana",
      "Gestión automática de facturas y cobros",
      "Respuestas a emails 24/7",
      "0 errores administrativos"
    ],
    targetAudience: ["Autónomos", "Administración", "Finanzas", "Operaciones"]
  },
  {
    id: "desarrollo",
    slug: "desarrollo",
    name: "Desarrollo",
    icon: Code,
    description: "Crea aplicaciones y sitios web sin programador. Ahorra 2.000-5.000€ por proyecto",
    longDescription: "Herramientas de IA que investigan, diseñan y programan aplicaciones completas. Desde webs corporativas hasta apps de gestión. Sin necesidad de contratar desarrolladores.",
    color: "from-green-500 to-emerald-500",
    benefits: [
      "Apps funcionales en minutos",
      "Webs profesionales sin código",
      "Ahorra 2.000-5.000€ por proyecto",
      "Desarrollo ilimitado gratis"
    ],
    targetAudience: ["Startups", "Emprendedores", "Dirección General"]
  },
  {
    id: "redes-sociales",
    slug: "redes-sociales",
    name: "Redes Sociales",
    icon: Share2,
    description: "Investiga, crea y publica contenido que mantiene tu voz personal en LinkedIn",
    longDescription: "Automatiza la investigación de tendencias y la creación de contenido para LinkedIn y otras redes. La IA investiga por ti y escribe manteniendo tu estilo único. Ahorra 18 horas mensuales.",
    color: "from-orange-500 to-red-500",
    benefits: [
      "Investigación automática de temas",
      "Posts con tu estilo personal",
      "Ahorra 18 horas al mes",
      "Herramientas 100% gratuitas"
    ],
    targetAudience: ["Marketing", "Consultores", "Freelance", "Directivos"]
  },
  {
    id: "presentaciones",
    slug: "presentaciones",
    name: "Presentaciones",
    icon: Presentation,
    description: "Crea presentaciones profesionales en 60 segundos. De la idea al slide deck completo",
    longDescription: "Genera presentaciones completas con diseño profesional, contenido estructurado y gráficos relevantes. Desde propuestas comerciales hasta formaciones internas. En menos de un minuto.",
    color: "from-indigo-500 to-purple-500",
    benefits: [
      "Presentaciones en 60 segundos",
      "Diseño profesional automático",
      "Propuestas comerciales al instante",
      "Formaciones sin preparación previa"
    ],
    targetAudience: ["Ventas", "Dirección", "Formación", "Consultores"]
  },
  {
    id: "atencion-cliente",
    slug: "atencion-cliente",
    name: "Atención al Cliente",
    icon: Phone,
    description: "Agentes de voz que atienden llamadas 24/7. Casos reales: +180% en pedidos telefónicos",
    longDescription: "Implementa agentes de voz con IA que atienden llamadas, toman pedidos y resuelven dudas las 24 horas. Casos de éxito: pizzería triplicó pedidos telefónicos sin aumentar plantilla.",
    color: "from-yellow-500 to-orange-500",
    benefits: [
      "Atención 24/7 sin descansos",
      "0 llamadas perdidas",
      "Casos reales: +180% en ventas",
      "Sin aumentar plantilla"
    ],
    targetAudience: ["Restaurantes", "Comercios", "Clínicas", "Talleres"]
  },
  {
    id: "productividad",
    slug: "productividad",
    name: "Productividad",
    icon: Target,
    description: "Analiza documentos, genera campañas de marketing y ejecuta tareas complejas al instante",
    longDescription: "Herramientas de IA que analizan documentos, generan estrategias de marketing completas y ejecutan tareas administrativas complejas. Todo en minutos, todo gratis.",
    color: "from-teal-500 to-green-500",
    benefits: [
      "Análisis instantáneo de documentos",
      "Campañas de marketing completas",
      "Ahorra 7.200€ anuales en agencias",
      "Tareas administrativas automatizadas"
    ],
    targetAudience: ["Gestorías", "Asesorías", "Marketing", "Administración"]
  },
  {
    id: "estrategia-ia",
    slug: "estrategia-ia",
    name: "Estrategia IA",
    icon: Compass,
    description: "Diagnostica tu negocio y planifica la ruta correcta para implementar IA con éxito",
    longDescription: "Antes de automatizar, necesitas saber en qué etapa estás. El 70% de proyectos de IA fracasan por falta de diagnóstico previo. Aprende a evaluar tu madurez digital y diseña una ruta práctica.",
    color: "from-amber-500 to-orange-500",
    benefits: [
      "ROI visible en 90 días",
      "45% menos errores de implementación",
      "Diagnóstico gratuito de madurez",
      "Ruta personalizada sin complicaciones"
    ],
    targetAudience: ["Dirección", "Operaciones", "Gestorías", "Consultoras"]
  },
  {
    id: "ciberseguridad",
    slug: "ciberseguridad",
    name: "Ciberseguridad",
    icon: Shield,
    description: "Protege tu negocio con IA: detección proactiva de amenazas, auditorías automáticas y cumplimiento normativo sin equipo técnico",
    longDescription: "La ciberseguridad ya no es solo para grandes empresas. Con herramientas de IA puedes monitorizar amenazas 24/7, auditar tu infraestructura, cumplir con el RGPD y simular ataques — todo sin contratar un departamento de seguridad.",
    color: "from-red-500 to-orange-500",
    benefits: [
      "Monitorización 24/7 sin equipo dedicado",
      "Detección proactiva de amenazas con IA",
      "Cumplimiento RGPD automatizado",
      "Simulación de ataques sin consultor externo"
    ],
    targetAudience: ["Dirección", "Tecnología", "Operaciones", "PYMEs"]
  },
  {
    id: "rincon-emprendedor",
    slug: "rincon-emprendedor",
    name: "El Rincón del Emprendedor",
    icon: Lightbulb,
    description: "Tendencias, reflexiones y visión de futuro para emprendedores que quieren ir un paso por delante",
    longDescription: "Un espacio editorial donde analizamos las tendencias tecnológicas que están transformando los negocios, compartimos reflexiones sobre el futuro del trabajo y te ayudamos a prepararte para los cambios que vienen. No es teoría: es visión práctica basada en lo que ya está pasando.",
    color: "from-violet-500 to-indigo-500",
    benefits: [
      "Anticípate a las tendencias antes que tu competencia",
      "Entiende el impacto real de la IA en tu sector",
      "Decisiones estratégicas con información fundamentada",
      "Visión de futuro sin humo ni hype"
    ],
    targetAudience: ["Emprendedores", "Directivos", "Startups", "Consultores"]
  }
];

export const getCategoryBySlug = (slug: string): Category | undefined => {
  return categories.find(cat => cat.slug === slug);
};
